import React from "react";
import { useLocation, useNavigate } from "react-router";

import {
  getCustomers,
  getEmergencyContacts,
} from "~/services/customerService";
import {
  buildCustomerPhoneDirectory,
  resolveCallCustomerIdentity,
} from "~/services/calling/customerMatcher";
import type { Customer, EmergencyContact } from "~/types/customer";
import { formatPhoneForDisplay, normalizePhoneNumber } from "~/utils/phone";
import type { IncomingCallInfo } from "~/services/calling/types";

import { hasEstablishedOperatorSession, type CallState } from "./callState";
import type { ScreenedIncomingCall, HeldCallEntry } from "./types";
import type { OutboundDialOptions } from "~/context/call/outboundCallLink";

/** Slice of call state needed for softphone UI (avoids circular dependency on callContext). */
export type CallStateSliceForSoftphone = Pick<
  CallState,
  | "incomingCalls"
  | "callDirection"
  | "callStatus"
  | "callUuid"
  | "callId"
  | "callReference"
  | "callerNumber"
  | "customerName"
  | "isOnHold"
  | "backgroundHeldCallUuid"
  | "backgroundHeldCallReference"
>;

type SetCallMetadata = (meta: {
  customerId?: string | null;
  callUuid?: string | null;
  callId?: string | null;
  callReference?: string | null;
  unitId?: string | null;
  callerNumber?: string | null;
  customerName?: string | null;
}) => void;

export function buildCallerDetailsUrl(params: {
  customerId?: string | null;
  callReference?: string | null;
  callNumber?: string | null;
  callUuid?: string | null;
  callMode?: "incoming" | "active" | "history";
}) {
  const search = new URLSearchParams();
  if (params.customerId) search.set("customerId", params.customerId);
  if (params.callReference) search.set("callReference", params.callReference);
  if (params.callNumber) search.set("callNumber", params.callNumber);
  if (params.callUuid) search.set("callUuid", params.callUuid);
  if (params.callMode) search.set("callMode", params.callMode);
  return `/caller-details?${search.toString()}`;
}

export type UseSoftphoneUiParams = {
  isAuthenticated: boolean;
  state: CallStateSliceForSoftphone;
  setCallMetadata: SetCallMetadata;
  answer: (incomingId?: string) => Promise<void>;
  rejectPendingInvitation: (incomingId?: string) => Promise<void>;
  hangup: () => Promise<void>;
  call: (target: string, options?: OutboundDialOptions) => Promise<void>;
  unhold: () => Promise<void>;
  reportError?: (message: string) => void;
};

export type UseSoftphoneUiResult = {
  customers: Customer[];
  directoryLoading: boolean;
  refreshCustomerDirectory: () => Promise<void>;
  activeCustomerId: string | null;
  activeCustomerName: string | null;
  activeCustomerNumber: string | null;
  activeMatchType: "customer" | "emergency-contact" | null;
  setActiveCustomerContext: (input: {
    customerId?: string | null;
    customerName?: string | null;
    callerNumber?: string | null;
    matchType?: "customer" | "emergency-contact" | null;
  }) => void;
  dismissedIncomingIds: string[];
  heldCalls: HeldCallEntry[];
  screenedIncomingCalls: ScreenedIncomingCall[];
  mostRecentIncoming: ScreenedIncomingCall | null;
  isOnDashboardRoute: boolean;
  dismissIncomingCall: (incomingId: string) => void;
  answerIncomingCall: (incomingId: string) => Promise<void>;
  declineIncomingCall: (incomingId: string) => Promise<void>;
  holdIncomingCall: (incomingId: string) => Promise<void>;
  resumeHeldCall: (heldId: string) => Promise<void>;
  startOutboundCustomerCall: (input: {
    customerId: string;
    customerName?: string | null;
    targetNumber: string;
    callReference?: string | null;
    matchType?: "customer" | "emergency-contact" | null;
  }) => Promise<void>;
};

export function useSoftphoneUi(
  params: UseSoftphoneUiParams
): UseSoftphoneUiResult {
  const {
    isAuthenticated,
    state,
    setCallMetadata,
    answer,
    rejectPendingInvitation,
    hangup,
    call,
    unhold,
    reportError,
  } = params;
  const navigate = useNavigate();
  const location = useLocation();

  const [customers, setCustomers] = React.useState<Customer[]>([]);
  const [directory, setDirectory] = React.useState<
    ReturnType<typeof buildCustomerPhoneDirectory>
  >([]);
  const [directoryLoading, setDirectoryLoading] = React.useState(false);
  const [activeCustomerId, setActiveCustomerId] = React.useState<string | null>(
    null
  );
  const [activeCustomerName, setActiveCustomerName] = React.useState<
    string | null
  >(null);
  const [activeCustomerNumber, setActiveCustomerNumber] = React.useState<
    string | null
  >(null);
  const [activeMatchType, setActiveMatchType] = React.useState<
    "customer" | "emergency-contact" | null
  >(null);
  const [dismissedIncomingIds, setDismissedIncomingIds] = React.useState<
    string[]
  >([]);
  const [heldCalls, setHeldCalls] = React.useState<HeldCallEntry[]>([]);

  const isOnDashboardRoute = location.pathname.startsWith("/dashboard");

  const setActiveCustomerContext = React.useCallback(
    (input: {
      customerId?: string | null;
      customerName?: string | null;
      callerNumber?: string | null;
      matchType?: "customer" | "emergency-contact" | null;
    }) => {
      const normalizedNumber = input.callerNumber
        ? normalizePhoneNumber(input.callerNumber) || input.callerNumber
        : null;
      setActiveCustomerId(input.customerId ?? null);
      setActiveCustomerName(input.customerName ?? null);
      setActiveCustomerNumber(normalizedNumber);
      setActiveMatchType(input.matchType ?? null);
    },
    []
  );

  const refreshCustomerDirectory = React.useCallback(async () => {
    if (!isAuthenticated) return;
    setDirectoryLoading(true);
    try {
      const fetchedCustomers = await getCustomers();
      const contactsByCustomerId: Record<string, EmergencyContact[]> = {};
      await Promise.all(
        fetchedCustomers.map(async (customer) => {
          const existingContacts = customer.emergencyContacts ?? [];
          if (existingContacts.length > 0) {
            contactsByCustomerId[customer.id] = existingContacts;
            return;
          }
          try {
            contactsByCustomerId[customer.id] = await getEmergencyContacts(
              customer.id
            );
          } catch {
            contactsByCustomerId[customer.id] = [];
          }
        })
      );
      setCustomers(fetchedCustomers);
      setDirectory(
        buildCustomerPhoneDirectory(fetchedCustomers, contactsByCustomerId)
      );
    } finally {
      setDirectoryLoading(false);
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setCustomers([]);
      setDirectory([]);
      setDismissedIncomingIds([]);
      setHeldCalls([]);
      setActiveCustomerContext({
        customerId: null,
        customerName: null,
        callerNumber: null,
        matchType: null,
      });
      return;
    }
    void refreshCustomerDirectory();
  }, [isAuthenticated, refreshCustomerDirectory, setActiveCustomerContext]);

  React.useEffect(() => {
    const url = new URLSearchParams(location.search);
    const customerId = url.get("customerId");
    const callNumber = url.get("callNumber");
    if (location.pathname !== "/caller-details" || !customerId) return;
    const customer = customers.find((c) => c.id === customerId);
    const customerName = customer
      ? [customer.firstName, customer.lastName].filter(Boolean).join(" ").trim()
      : null;
    setActiveCustomerContext({
      customerId,
      customerName,
      callerNumber: callNumber,
      matchType: null,
    });
    setCallMetadata({
      customerId,
      customerName,
      callerNumber: callNumber,
    });
  }, [
    customers,
    location.pathname,
    location.search,
    setActiveCustomerContext,
    setCallMetadata,
  ]);

  const screenedIncomingCalls = React.useMemo<ScreenedIncomingCall[]>(() => {
    const heldIncomingIds = new Set(
      heldCalls
        .filter((item) => item.holdSource === "incoming-call")
        .map((item) => item.incomingId)
        .filter(Boolean)
    );
    return state.incomingCalls
      .filter(
        (call) =>
          !dismissedIncomingIds.includes(call.id) && !heldIncomingIds.has(call.id)
      )
      .map((call) => {
        const identity = resolveCallCustomerIdentity(call, directory);

        return {
          ...call,
          ...identity,
          displayNumber: formatPhoneForDisplay(call.callerNumber),
        };
      })
      .filter(Boolean) as ScreenedIncomingCall[];
  }, [state.incomingCalls, directory, dismissedIncomingIds, heldCalls]);

  React.useEffect(() => {
    setDismissedIncomingIds((prev) =>
      prev.filter((id) => state.incomingCalls.some((call) => call.id === id))
    );
  }, [state.incomingCalls]);

  const mostRecentIncoming = React.useMemo(() => {
    if (screenedIncomingCalls.length === 0) return null;
    return [...screenedIncomingCalls].sort(
      (a, b) => b.receivedAtMs - a.receivedAtMs
    )[0];
  }, [screenedIncomingCalls]);

  React.useEffect(() => {
    if (!mostRecentIncoming) return;
    if (hasEstablishedOperatorSession(state)) return;

    if (location.pathname === "/caller-details") {
      const urlCallUuid = new URLSearchParams(location.search)
        .get("callUuid")
        ?.trim();
      if (urlCallUuid) return;
    }

    if (state.callDirection !== "incoming" || state.callStatus !== "ringing") {
      return;
    }

    const normalizedCaller =
      normalizePhoneNumber(mostRecentIncoming.callerNumber) ||
      mostRecentIncoming.callerNumber;
    setCallMetadata({
      customerId: mostRecentIncoming.customerId ?? null,
      customerName: mostRecentIncoming.customerName ?? null,
      callerNumber: normalizedCaller ?? null,
      callReference: mostRecentIncoming.service ?? null,
      callUuid: mostRecentIncoming.callUuid ?? null,
      callId: mostRecentIncoming.callId ?? null,
    });
  }, [
    location.pathname,
    location.search,
    state.callDirection,
    state.callStatus,
    mostRecentIncoming,
    setCallMetadata,
  ]);

  const dismissIncomingCall = React.useCallback((incomingId: string) => {
    setDismissedIncomingIds((prev) =>
      prev.includes(incomingId) ? prev : [...prev, incomingId]
    );
  }, []);

  const answerIncomingCall = React.useCallback(
    async (incomingId: string) => {
      const incoming = screenedIncomingCalls.find((call) => call.id === incomingId);
      await answer(incomingId);
      if (!incoming) return;
      const normalizedCaller =
        normalizePhoneNumber(incoming.callerNumber) || incoming.callerNumber;
      setActiveCustomerContext({
        customerId: incoming.customerId,
        customerName: incoming.customerName,
        callerNumber: normalizedCaller,
        matchType: incoming.matchType,
      });
      setCallMetadata({
        customerId: incoming.customerId ?? null,
        customerName: incoming.customerName ?? null,
        callerNumber: normalizedCaller ?? null,
        callReference: incoming.service ?? null,
        callUuid: incoming.callUuid ?? null,
        callId: incoming.callId ?? null,
      });
      navigate(
        buildCallerDetailsUrl({
          customerId: incoming.customerId,
          callReference: incoming.service,
          callNumber: normalizedCaller,
          callUuid: incoming.callUuid,
          callMode: "active",
        })
      );
    },
    [
      answer,
      navigate,
      screenedIncomingCalls,
      setActiveCustomerContext,
      setCallMetadata,
    ]
  );

  const declineIncomingCall = React.useCallback(
    async (incomingId: string) => {
      dismissIncomingCall(incomingId);
      try {
        await rejectPendingInvitation(incomingId);
      } catch {
        // Keep UI stable when backend rejects decline.
      }
    },
    [rejectPendingInvitation, dismissIncomingCall]
  );

  const holdIncomingCall = React.useCallback(
    (incomingId: string) => {
      const incoming = screenedIncomingCalls.find((call) => call.id === incomingId);
      if (!incoming) return Promise.resolve();
      const normalizedCaller =
        normalizePhoneNumber(incoming.callerNumber) || incoming.callerNumber;
      const holdEntryId = `incoming-hold-${incoming.id}`;
      setHeldCalls((prev) => {
        if (prev.some((item) => item.id === holdEntryId)) return prev;
        return [
          {
            id: holdEntryId,
            holdSource: "incoming-call",
            incomingId: incoming.id,
            matchType: incoming.matchType,
            callUuid: incoming.callUuid,
            callId: incoming.callId,
            callReference: incoming.service,
            unitId: incoming.unitId,
            callerNumber: normalizedCaller,
            customerId: incoming.customerId,
            customerName: incoming.customerName,
            heldAtMs: Date.now(),
          },
          ...prev,
        ];
      });
      dismissIncomingCall(incomingId);
      return Promise.resolve();
    },
    [dismissIncomingCall, screenedIncomingCalls]
  );

  React.useEffect(() => {
    if (state.callStatus === "ended") {
      setHeldCalls((prev) =>
        prev.filter((item) => item.holdSource === "incoming-call")
      );
      return;
    }

    const backgroundHeldCallUuid = state.backgroundHeldCallUuid;
    const heldCallUuid =
      backgroundHeldCallUuid ?? state.callUuid ?? state.callId ?? null;
    const holdId = `active-hold-${
      heldCallUuid ??
      state.callReference ??
      `held-${state.callerNumber ?? "unknown"}`
    }`;
    const keepActiveHoldRow =
      state.isOnHold || Boolean(backgroundHeldCallUuid);

    if (keepActiveHoldRow) {
      setHeldCalls((prev) => {
        const existing =
          prev.find((item) => item.id === holdId) ??
          prev.find(
            (item) =>
              item.holdSource === "active-call" &&
              Boolean(
                heldCallUuid &&
                  (item.callUuid === heldCallUuid || item.callId === heldCallUuid)
              )
          );
        const preservedCallerNumber =
          backgroundHeldCallUuid && existing?.callerNumber
            ? existing.callerNumber
            : state.callerNumber;
        const preservedCustomerName =
          backgroundHeldCallUuid && existing?.customerName
            ? existing.customerName
            : activeCustomerName ?? state.customerName ?? null;
        const preservedCustomerId =
          backgroundHeldCallUuid && existing?.customerId
            ? existing.customerId
            : activeCustomerId;
        const preservedCallReference =
          state.backgroundHeldCallReference ??
          existing?.callReference ??
          state.callReference;

        if (existing) {
          return prev.map((item) =>
            item.id === existing.id
              ? {
                  ...item,
                  id: holdId,
                  holdSource: "active-call",
                  callUuid: heldCallUuid ?? item.callUuid,
                  callId: state.callId ?? item.callId,
                  callReference: preservedCallReference ?? null,
                  callerNumber: preservedCallerNumber ?? null,
                  customerId: preservedCustomerId,
                  customerName: preservedCustomerName,
                }
              : item
          );
        }

        return [
          {
            id: holdId,
            holdSource: "active-call",
            callUuid: heldCallUuid,
            callId: state.callId,
            callReference: preservedCallReference ?? null,
            callerNumber: preservedCallerNumber ?? null,
            customerId: preservedCustomerId,
            customerName: preservedCustomerName,
            heldAtMs: Date.now(),
          },
          ...prev,
        ];
      });
      return;
    }

    setHeldCalls((prev) => prev.filter((item) => item.id !== holdId));
  }, [
    activeCustomerId,
    activeCustomerName,
    state.backgroundHeldCallReference,
    state.backgroundHeldCallUuid,
    state.callReference,
    state.callStatus,
    state.callId,
    state.callUuid,
    state.callerNumber,
    state.customerName,
    state.isOnHold,
  ]);

  React.useEffect(() => {
    const incomingIds = new Set(state.incomingCalls.map((call) => call.id));
    setHeldCalls((prev) =>
      prev.filter((item) => {
        if (item.holdSource !== "incoming-call") return true;
        return Boolean(item.incomingId && incomingIds.has(item.incomingId));
      })
    );
  }, [state.incomingCalls]);

  const resumeHeldCall = React.useCallback(
    async (heldId: string) => {
      const held = heldCalls.find((item) => item.id === heldId);
      if (!held) return;

      const hasForegroundOutbound =
        held.holdSource === "active-call" &&
        state.callDirection === "outgoing" &&
        (state.callStatus === "active" || state.callStatus === "dialing");

      if (hasForegroundOutbound) {
        reportError?.("End your outbound call before resuming the held call.");
        return;
      }

      if (held.holdSource === "incoming-call" && held.incomingId) {
        await answer(held.incomingId);
      } else {
        await unhold();
      }
      setHeldCalls((prev) => prev.filter((item) => item.id !== heldId));
      setActiveCustomerContext({
        customerId: held.customerId,
        customerName: held.customerName,
        callerNumber: held.callerNumber,
        matchType: held.matchType ?? activeMatchType,
      });
      setCallMetadata({
        customerId: held.customerId ?? null,
        customerName: held.customerName ?? null,
        callerNumber: held.callerNumber ?? null,
        callReference: held.callReference ?? null,
        callUuid: held.callUuid ?? null,
        callId: held.callId ?? null,
      });
      navigate(
        buildCallerDetailsUrl({
          customerId: held.customerId,
          callReference: held.callReference,
          callNumber: held.callerNumber,
          callUuid: held.callUuid,
          callMode: "active",
        })
      );
    },
    [
      activeMatchType,
      answer,
      heldCalls,
      navigate,
      setActiveCustomerContext,
      setCallMetadata,
      state.callDirection,
      state.callStatus,
      reportError,
      unhold,
    ]
  );

  const startOutboundCustomerCall = React.useCallback(
    async (input: {
      customerId: string;
      customerName?: string | null;
      targetNumber: string;
      callReference?: string | null;
      matchType?: "customer" | "emergency-contact" | null;
    }) => {
      const normalizedTarget =
        normalizePhoneNumber(input.targetNumber) || input.targetNumber;
      setActiveCustomerContext({
        customerId: input.customerId,
        customerName: input.customerName ?? null,
        callerNumber: normalizedTarget,
        matchType: input.matchType ?? "customer",
      });
      setCallMetadata({
        customerId: input.customerId,
        callerNumber: normalizedTarget,
        customerName: input.customerName ?? null,
        callReference: input.callReference ?? null,
      });
      await call(normalizedTarget, { customerId: input.customerId });
      navigate(
        buildCallerDetailsUrl({
          customerId: input.customerId,
          callReference: input.callReference ?? null,
          callNumber: normalizedTarget,
          callMode: "active",
        })
      );
    },
    [call, navigate, setActiveCustomerContext, setCallMetadata]
  );

  return {
    customers,
    directoryLoading,
    refreshCustomerDirectory,
    activeCustomerId,
    activeCustomerName,
    activeCustomerNumber,
    activeMatchType,
    setActiveCustomerContext,
    dismissedIncomingIds,
    heldCalls,
    screenedIncomingCalls,
    mostRecentIncoming,
    isOnDashboardRoute,
    dismissIncomingCall,
    answerIncomingCall,
    declineIncomingCall,
    holdIncomingCall,
    resumeHeldCall,
    startOutboundCustomerCall,
  };
}
